import { defineComponent as _defineComponent } from 'vue'
import { defineMessages } from '@/lib/i18n';
import { PropType, computed, ref, watch, nextTick, onMounted } from 'vue';
import _ from 'lodash';
import { now } from '@/utils/datetime';
import sharedMessages from '@/lib/shared-messages';

import {
  SdsProduct,
  SdsSection2,
  SdsSection3Material,
} from '@/interfaces/model/admin/sds';
import { Section2ClassificationSystemId } from '@/lib/enum';
import SdsTableSection2List from '@/views/dashboard/sdsDrawer/SdsTableSection2List.vue';
import { readUserLocale } from '@/store/main/getters';
import { store } from '@/store/index';
import { i18n } from '@/plugins/vue-i18n';
import { required } from '@/lib/rule';
import { VForm } from 'vuetify/lib/components';
import { getSection2GHSCategoryList } from '@/lib/formItem';
import { useMSdsSection2 } from '@/composables/useMSdsSection2';

export type SdsSection2WithIndex = SdsSection2 & {
  section2_list_index?: number;
};

type ClassificationSystemId = (typeof ClassificationSystemIds)[string];

type ClassificationForm = {
  classification_system_id?: ClassificationSystemId | null;
  isOpen: boolean;
  isOpenOverall: boolean;
  hasOveralls: boolean;
  hasMaterials: boolean;
  materialForms: Array<{
    material_name?: number | string;
    isOpen: boolean;
  }>;
};


export default /*#__PURE__*/_defineComponent({
  __name: 'SdsTableSection2GHS',
  props: {
  sdsSection2List: {
    type: Array as PropType<Array<SdsSection2>>,
    required: true,
  },
  section3MaterialList: {
    type: Array as PropType<Array<SdsSection3Material>>,
    required: true,
  },
  isEditing: {
    type: Boolean,
    default: false,
  },
  isLoadingSds: {
    type: Boolean,
    default: false,
  },
  productIndex: {
    type: Number,
    default: 0,
  },
  products: {
    type: Array as PropType<Array<SdsProduct>>,
    default: () => [],
  },
},
  emits: ["update:sdsSection2List", "update:isValid"],
  setup(__props, { emit: emits }: { emit: ({
  (e: 'update:sdsSection2List', data: Array<SdsSection2>): void;
  (e: 'update:isValid', data: boolean): void;
}), expose: any, slots: any, attrs: any }) {

const props = __props;

const $t = i18n.t.bind(i18n);
const locale = computed(() => readUserLocale(store));
const textProperty = locale.value === 'ja-JP' ? 'ja' : 'en';

const m = defineMessages({
  addClassificationSystem: '分類体系の追加',
  addMaterial: '含有成分の追加',
  overallProduct: '製品全体',
});





const form = ref<VForm | null>(null);

const getCategoryList = (item_id) =>
  getSection2GHSCategoryList(mSdsSection2Categories.value).filter(
    (x) => x.itemId == item_id
  );
const getNotStatedCategory = (itemId) =>
  getCategoryList(itemId).find((c) =>
    c.text.includes('該非記載なし ; Not stated')
  );
const isValidClassificationForm = ref<boolean>(false);
const showClassificationModal = ref<boolean>(false);
const selectedClassificationFormId = ref<string | null>();
const selectedClassificationFormIndex = ref<number>(-1);
const toggleClassificationModal = () => {
  showClassificationModal.value = !showClassificationModal.value;
};

const isValidMaterialForm = ref<boolean>(false);
const showMaterialModal = ref<boolean>(false);
const materialFormValue = ref<string | null>();
const selectedMaterialFormIndex = ref<number>(-1);
const toggleMaterialModal = () => {
  showMaterialModal.value = !showMaterialModal.value;
};

const groupSortSection2List = (
  filter: (s: SdsSection2) => boolean,
  grouper: (s: SdsSection2) => string
): Record<number | string, SdsSection2WithIndex[]> => {
  const withIndex = props.sdsSection2List.map((s, i) => ({
    ...s,
    section2_list_index: i,
  }));
  const grouped = _.groupBy(withIndex.filter(filter), grouper);
  // 本来サーバー側で順番を制御しているが、
  // アルバイトさんがデータを追加したら順番がバラバラになるので、
  // ここでもソートする
  Object.values(grouped).forEach((arr) => {
    arr.sort((a, b) => a.item_id - b.item_id);
  });
  return grouped;
};

const overallSection2List = computed<Record<number, SdsSection2WithIndex[]>>(
  () =>
    groupSortSection2List(
      (s) => !s.material_name,
      (s) => s.classification_system_id ?? Section2ClassificationSystemId.GHS.id
    )
);
const materialSection2List = computed<
  Record<number, Record<number, SdsSection2WithIndex[]>>
>(() => {
  // first level: by classification_system_id
  const classificationGroup = groupSortSection2List(
    (s) => !!s.material_name,
    (s) => s.classification_system_id ?? Section2ClassificationSystemId.GHS.id
  );

  // second level: group each of classification_system_id by section3_material_id
  const materialGroup = Object.fromEntries(
    Object.entries(classificationGroup).map(([sysId, arr]) => [
      sysId,
      _.mapValues(
        _.groupBy(arr, (s) => s.material_name as number),
        (bucket) => bucket.slice().sort((a, b) => a.item_id - b.item_id)
      ),
    ])
  );
  return materialGroup;
});

const deleteRecoverOverallSection2List = (
  classificationSystemId: string,
  recover: boolean
) => {
  const bucket = overallSection2List.value[classificationSystemId];
  if (!bucket) return;

  const updated = [...props.sdsSection2List];

  bucket.forEach((s) => {
    updated[s.section2_list_index].deleted_at = recover ? null : now().toISO();
  });

  // recover missing items
  if (recover) {
    const requiredItems = getItemsByClassificationSystemId(
      classificationSystemId
    );

    requiredItems.forEach((x) => {
      const exists = updated.some(
        (r) =>
          r.item_id === x.id &&
          r.classification_system_id === classificationSystemId &&
          r.deleted_at === null
      );

      if (!exists) {
        updated.push({
          id: 0,
          sds_id: '',
          sds_product_id: 0,
          item_id: x.id,
          category_id: 0,
          sds_section2_hazard_list: [],
          classification_system_id: classificationSystemId,
          material_name: null,
          created_at: '',
          updated_at: '',
          deleted_at: null,
        });
      }
    });
  }

  updateSdsSection2List(updated);
};

const deleteRecoverMaterialSection2List = (
  classificationSystemId: string,
  materialName: string,
  recover: boolean
) => {
  const sysGroup = materialSection2List.value[classificationSystemId];
  if (!sysGroup) return;

  const bucket = sysGroup[String(materialName)];
  if (!bucket) return;

  const updated = [...props.sdsSection2List];

  bucket.forEach((s) => {
    updated[s.section2_list_index].deleted_at = recover ? null : now().toISO();
  });
  updateSdsSection2List(updated);
};

const purgeMaterialSection2List = (
  classificationSystemId: string,
  materialName: string
) => {
  const sysGroup = materialSection2List.value[classificationSystemId];
  if (!sysGroup) return;
  const bucket = sysGroup[String(materialName)];
  if (!bucket) return;
  const updated = [...props.sdsSection2List];
  const purgeIndexes = bucket
    .map((s) => s.section2_list_index)
    .sort((a, b) => b - a);
  purgeIndexes.forEach((idx) => updated.splice(idx, 1));
  updateSdsSection2List(updated);
};

const ClassificationSystemIds = Section2ClassificationSystemId.values().map(
  (s) => s.id
) as const;
const getInitialClassificationForm = (
  classificationSystemId: string,
  classificationOptions?: ClassificationForm
) => {
  const materialForms =
    Object.keys(materialSection2List.value[classificationSystemId] || {})?.map(
      (id) => ({
        material_name: String(id),
        isOpen: false,
      })
    ) || [];
  return {
    classification_system_id: classificationSystemId,
    isOpen: true,
    isOpenOverall: false,
    hasOveralls: !!overallSection2List.value[classificationSystemId]?.length,
    hasMaterials: !!materialForms.length,
    materialForms,
    ...(classificationOptions || {}),
  };
};
const CLASSIFICATION_ORDER = {
  GHS: 1,
  CLP: 2,
  OSHA: 3, // OSHA(HCS)
  JIS: 4,
  OTHER: 99, // fallback for その他 or unknown
};

const getInitialClassificationForms = () =>
  Array.from(
    new Set(
      props.sdsSection2List.map(
        (item) =>
          item.classification_system_id || Section2ClassificationSystemId.GHS.id
      )
    )
  )
    .sort((a, b) => {
      const rankA = CLASSIFICATION_ORDER[a] ?? CLASSIFICATION_ORDER.OTHER;
      const rankB = CLASSIFICATION_ORDER[b] ?? CLASSIFICATION_ORDER.OTHER;
      return rankA - rankB;
    })
    .map((classification_system_id, index) =>
      getInitialClassificationForm(
        classification_system_id,
        index === 0 ? { isOpenOverall: true } : undefined
      )
    );

const classificationForms = ref<ClassificationForm[]>(
  getInitialClassificationForms()
);

const shouldDisableAddClassification = computed<boolean>(() => {
  const lastClassificationForm =
    classificationForms.value[classificationForms.value.length - 1];
  return (
    (classificationForms.value.length &&
      !lastClassificationForm?.classification_system_id) ||
    classificationForms.value.length >=
      Section2ClassificationSystemId.values().length
  );
});
const upsertClassificationForm = (): void => {
  if (!form.value.validate()) return;
  if (shouldDisableAddClassification.value) return;
  const id = selectedClassificationFormId.value;
  if (selectedClassificationFormIndex.value === -1) {
    // add
    classificationForms.value.push({
      classification_system_id: id,
      isOpen: true,
      isOpenOverall: true,
      hasOveralls: true,
      hasMaterials: false,
      materialForms: [],
    });
    const sdsSection2List = props.sdsSection2List;

    onChangeClassificationSystemId(classificationForms.value.length - 1, id);
    getItemsByClassificationSystemId(id).forEach((x) => {
      sdsSection2List.push({
        id: 0,
        sds_id: '',
        sds_product_id: 0,
        item_id: x.id,
        category_id: getNotStatedCategory(x.id)?.id || 0,
        sds_section2_hazard_list: [],
        classification_system_id: id,
        material_name: null,
        created_at: '',
        updated_at: '',
        deleted_at: null,
      });
    });
    updateSdsSection2List(sdsSection2List);
  } else {
    // edit
    onChangeClassificationSystemId(selectedClassificationFormIndex.value, id);
  }
  toggleClassificationModal();
};

const onChangeClassificationSystemId = (index: number, newId: string) => {
  const prevClassificationForm = classificationForms.value[index];
  // delete
  deleteRecoverOverallSection2List(
    prevClassificationForm.classification_system_id,
    false
  );
  prevClassificationForm.materialForms.forEach((materialForm) => {
    deleteRecoverMaterialSection2List(
      prevClassificationForm.classification_system_id,
      materialForm.material_name,
      false
    );
  });
  if (newId) {
    classificationForms.value.splice(
      index,
      1,
      getInitialClassificationForm(newId, { isOpenOverall: true })
    );
    // recover
    deleteRecoverOverallSection2List(newId, true);
    classificationForms.value[index].materialForms.forEach((materialForm) => {
      deleteRecoverMaterialSection2List(
        newId,
        materialForm.material_name,
        true
      );
    });
  } else {
    // minus button
    classificationForms.value.splice(index, 1);
    // set default to GHS when only one and empty
    if (
      classificationForms.value.length === 1 &&
      !classificationForms.value[0].classification_system_id
    ) {
      onChangeClassificationSystemId(
        0,
        classificationForms.value[0],
        Section2ClassificationSystemId.GHS.id
      );
    }
  }
  classificationForms.value.sort((a, b) => {
    const rankA =
      CLASSIFICATION_ORDER[a.classification_system_id] ??
      CLASSIFICATION_ORDER.OTHER;
    const rankB =
      CLASSIFICATION_ORDER[b.classification_system_id] ??
      CLASSIFICATION_ORDER.OTHER;
    return rankA - rankB;
  });
};

const updateMaterialSection2List = (
  classificationSystemId: string,
  prevMaterialName: string,
  newMaterialName: string
) => {
  const sysGroup = materialSection2List.value[classificationSystemId];
  if (!sysGroup) return;

  const bucket = sysGroup[prevMaterialName];
  if (!bucket) return;

  const updated = [...props.sdsSection2List];

  bucket.forEach((s) => {
    updated[s.section2_list_index].material_name = newMaterialName;
  });

  updateSdsSection2List(updated);
};

const upsertMaterialForm = (): void => {
  if (!isValidMaterialForm.value) return;
  const cfIndex = selectedClassificationFormIndex.value;
  const mIndex = selectedMaterialFormIndex.value;
  const classificationForm = classificationForms.value[cfIndex];
  const prevMaterialForm = classificationForm.materialForms[mIndex];
  if (prevMaterialForm?.material_name === materialFormValue.value)
    return toggleMaterialModal();

  if (selectedMaterialFormIndex.value === -1) {
    // add
    classificationForms.value[cfIndex].materialForms.push({
      material_name: materialFormValue.value,
      isOpen: true,
    });
    // fill materials when there is nothing
    if (
      !materialSection2List.value[
        classificationForm.classification_system_id
      ]?.[materialFormValue.value]?.length
    ) {
      const sdsSection2List = props.sdsSection2List;
      getItemsByClassificationSystemId(
        classificationForm.classification_system_id
      ).forEach((x) => {
        sdsSection2List.push({
          id: 0,
          sds_id: '',
          sds_product_id: 0,
          item_id: x.id,
          category_id: getNotStatedCategory(x.id)?.id || 0,
          sds_section2_hazard_list: [],
          classification_system_id: classificationForm.classification_system_id,
          material_name: materialFormValue.value,
          created_at: '',
          updated_at: '',
          deleted_at: null,
        });
      });
      updateSdsSection2List(sdsSection2List);
    } else {
      // recover
      deleteRecoverMaterialSection2List(
        classificationForm.classification_system_id,
        materialFormValue.value,
        true
      );
    }
  } else {
    purgeMaterialSection2List(
      classificationForm.classification_system_id,
      materialFormValue.value
    );
    // edit
    classificationForms.value[cfIndex].materialForms.splice(mIndex, 1, {
      material_name: materialFormValue.value,
      isOpen: true,
    });
    nextTick(() => {
      updateMaterialSection2List(
        classificationForm.classification_system_id,
        prevMaterialForm.material_name,
        materialFormValue.value
      );
    });
  }

  toggleMaterialModal();
};

const isDisabledClassificationSystem = (index, option) => {
  return classificationForms.value.some(
    (c, i) => i !== index && c.classification_system_id === option?.id
  );
};

const isDisabledMaterial = (
  classificationFormsIndex: number,
  index: number,
  option
) => {
  return classificationForms.value[classificationFormsIndex].materialForms.some(
    (c, i) => i !== index && String(c.material_name) === String(option?.value)
  );
};

const updateSdsSection2List = (data: SdsSection2[]): void => {
  emits('update:sdsSection2List', structuredClone(data));
};

watch(
  [() => props.isLoadingSds, () => props.productIndex],
  ([nowIsLoading, newIndex], [oldIsLoading, oldIndex]) => {
    if ((oldIsLoading && !nowIsLoading) || newIndex !== oldIndex) {
      classificationForms.value.splice(
        0,
        classificationForms.value.length,
        ...getInitialClassificationForms()
      );
    }
  }
);

const section3MaterialDropdownItems = computed(() => {
  let items = (props.section3MaterialList || []).filter(
    (material) => !material.deleted_at
  );
  items = items.map((item) => {
    return {
      text: item.material_name,
      value: item.material_name,
    };
  });
  return [...items];
});

const {
  categories: mSdsSection2Categories,
  load: loadMSection2,
  getItemsByClassificationSystemId,
} = useMSdsSection2();

onMounted(async () => {
  await loadMSection2();
});

return { __sfc: true,$t, locale, textProperty, m, props, emits, form, getCategoryList, getNotStatedCategory, isValidClassificationForm, showClassificationModal, selectedClassificationFormId, selectedClassificationFormIndex, toggleClassificationModal, isValidMaterialForm, showMaterialModal, materialFormValue, selectedMaterialFormIndex, toggleMaterialModal, groupSortSection2List, overallSection2List, materialSection2List, deleteRecoverOverallSection2List, deleteRecoverMaterialSection2List, purgeMaterialSection2List, ClassificationSystemIds, getInitialClassificationForm, CLASSIFICATION_ORDER, getInitialClassificationForms, classificationForms, shouldDisableAddClassification, upsertClassificationForm, onChangeClassificationSystemId, updateMaterialSection2List, upsertMaterialForm, isDisabledClassificationSystem, isDisabledMaterial, updateSdsSection2List, section3MaterialDropdownItems, mSdsSection2Categories, loadMSection2, getItemsByClassificationSystemId, sharedMessages, Section2ClassificationSystemId, SdsTableSection2List, required, VForm }
}

})